import { CalendarViewModel, ProfileViewModel } from '@views';
import { action, computed, makeObservable, observable } from 'mobx';
import { Inject, Service } from 'typedi';
import { ScheduleCalendarRequest } from '../api';
import { TCalendarMeetingsColor } from '../types';
import { getCorrectISOString } from '../helpers/getCorrectISOString';
import { addMonths, endOfMonth, startOfMonth, subMonths, startOfDay, endOfDay } from 'date-fns';
import { months } from '../constants';

@Service()
export class MiniCalendarService {

  @Inject(() => ScheduleCalendarRequest)
  private scheduleRequest: ScheduleCalendarRequest;

  @Inject(() => CalendarViewModel)
  private calendarViewModel: CalendarViewModel;

  @Inject(() => ProfileViewModel)
  private profileViewModel: ProfileViewModel;

  /** Цвета митингов, для отображения в мини-календаре */
  @observable.shallow calendarMeetingsColor: TCalendarMeetingsColor = new Map();

  /** Начальная дата в мини-календаре (начало месяца) */
  @observable startInterval: Date = startOfMonth(new Date());

  /** Конечная дата в мини-календаре (конец месяца) */
  @observable endInterval: Date = endOfMonth(new Date());

  @observable selectedDate: Date = null;

  constructor() {
    makeObservable(this);
  }

  /** Месяц отображаемый в мини-календаре */
  @computed get currentMonth() {
    return `${months[this.startInterval.getMonth()]}, ${this.startInterval.getFullYear()}`;
  }

  /** Сегодняшний день */
  @computed get today() {
    return this.calendarViewModel.today;
  }

  /**
   * Функция обновления стартовой и конечной даты мини-календаря
   * @param date Дата, от которой возьмется начало и конец месяца
   */
  @action updateInterval = (date: Date) => {
    this.startInterval = startOfMonth(date);
    this.endInterval = endOfMonth(date);
  }

  /**
   * Функция выбора даты в мини-календаре
   * @param date Дата на которую ткнул пользователь
   */
  @action selectDate = (date: Date) => {
    this.selectedDate = date;
  }

  /**
   * Перелистнуть к текущему месяцу
   */
  @action toCurrentMonth = () => {
    this.startInterval = startOfMonth(this.today);
    this.endInterval= endOfMonth(this.today);
  }

  /** Перелистнуть мини-календарь на следующий месяц */
  nextMonth = () => {
    this.calendarMeetingsColor.clear();
    const newCalendarData = addMonths(this.startInterval, 1);
    this.updateInterval(newCalendarData);
  }

  /** Перелистнуть мини-календарь на предыдущий месяц */
  prevMonth = () => {
    this.calendarMeetingsColor.clear();
    const newCalendarData = subMonths(this.startInterval, 1);
    this.updateInterval(newCalendarData);
  }

  /** Функция получения цветов митингов */
  @action getMeetingColors = () => {
    const startInterval = getCorrectISOString(startOfDay(this.startInterval), this.profileViewModel.minutesOffset);
    const endInterval = getCorrectISOString(endOfDay(this.endInterval), this.profileViewModel.minutesOffset);
    this.scheduleRequest.getMeetingsColors(startInterval, endInterval)
      .then((resp) => {
        const colors: TCalendarMeetingsColor = new Map();
        resp.data.forEach((elem) => colors.set(
          new Date(elem.date).toDateString(),
          elem.colorHexCodes
        ));
        this.calendarMeetingsColor = colors;
      });
  }
}
